/* eslint-disable @next/next/no-img-element */
'use client';

import WarningCard from '@/components/common/warning-card';
import AlertMessage from '@/components/ui/alert-message';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { imageSrc } from '@/lib/imageSrc';
import { cn } from '@/lib/utils';
import { useCreateAppointmentMutation } from '@/redux/api/appointmentApi';
import { useGetDoctorsQuery } from '@/redux/api/doctorApi';
import { selectCurrentUser } from '@/redux/features/authSlice';
import { useAppSelector } from '@/redux/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { CalendarIcon, Loader2, LoaderCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// Function to generate time slots
const generateTimeSlots = (start: string, end: string, interval: number) => {
  const parseTime = (time: string) => {
    const [hour, minutePart] = time.split(':');
    const minutes = parseInt(minutePart.slice(0, 2), 10);
    const isPM = minutePart.toLowerCase().includes('pm');
    const hours = (parseInt(hour, 10) % 12) + (isPM ? 12 : 0);
    return new Date(1970, 0, 1, hours, minutes, 0);
  };

  const startHour = parseTime(start);
  const endHour = parseTime(end);
  const slots: string[] = [];

  while (startHour < endHour) {
    const startSlot = new Date(startHour);
    const endSlot = new Date(startHour);
    endSlot.setMinutes(startSlot.getMinutes() + interval);

    const formatTime = (date: Date) => {
      const hour = date.getHours() % 12 || 12;
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
      return `${hour}:${minutes} ${ampm}`;
    };

    if (endSlot <= endHour) {
      slots.push(`${formatTime(startSlot)} to ${formatTime(endSlot)}`);
    }

    startHour.setMinutes(startHour.getMinutes() + interval);
  }

  return slots;
};

const AppointmentFormSchema = z.object({
  fullName: z
    .string({ required_error: 'Full Name is required' })
    .min(3, {
      message: 'Full Name should contain at least 3 characters',
    })
    .max(50, {
      message: 'Full Name should contain at most 50 characters',
    }),
  email: z.string({ required_error: 'Email is required' }).email({
    message: 'Invalid email format',
  }),
  phone: z
    .string({ required_error: 'Phone is required' })
    .min(4, {
      message: 'Phone should contain at least 10 characters',
    })
    .max(15, {
      message: 'Phone should contain at most 15 characters',
    }),
  sex: z.enum(['male', 'female', 'other'], {
    required_error: 'Sex is required',
  }),
  age: z
    .number({ required_error: 'Age is required' })
    .min(0, { message: 'Age must be a positive number' })
    .max(150, { message: 'Age must be less than 150' }),
  doctorId: z.string({
    required_error: 'Doctor is required',
  }),
  date: z.date({
    required_error: 'Date is required',
  }),
  time: z.string({
    required_error: 'Time is required',
  }),
  message: z
    .string({ required_error: 'Message is required' })
    .min(10, { message: 'Message should contain at least 10 characters' })
    .max(500, { message: 'Message should contain at most 500 characters' }),
});

const AppointmentForm = () => {
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<any>(null);
  const user = useAppSelector(selectCurrentUser);
  const router = useRouter();
  const {
    data,
    isError: dIsError,
    isLoading: dIsLoading,
  } = useGetDoctorsQuery(undefined);
  const doctors = data?.body;
  const [Appointment, { isLoading, isError, isSuccess, error }] =
    useCreateAppointmentMutation();

  useEffect(() => {
    if (selectedDoctor) {
      const [startTime, endTime] =
        selectedDoctor.availability.time.split(' - ');
      const slots = generateTimeSlots(startTime, endTime, 30);
      setTimeSlots(slots);
    }
  }, [selectedDoctor]);

  const form = useForm<z.infer<typeof AppointmentFormSchema>>({
    resolver: zodResolver(AppointmentFormSchema),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      sex: undefined,
      age: undefined,
      doctorId: '',
      date: undefined,
      time: '',
      message: '',
    },
  });

  async function onSubmit(values: z.infer<typeof AppointmentFormSchema>) {
    if (!user) {
      toast.error('Please log in to book an appointment', {
        description() {
          return (
            <div className="flex flex-col gap-2 bg-red-50 p-4 text-red-800">
              <p>
                You need to be logged in to request an appointment.
                <br />
                If you don&apos;t have an account, please create one to
                continue.
              </p>
              <Button variant="link" onClick={() => router.push('/register')}>
                Create an Account
              </Button>
            </div>
          );
        },
      });
      // set timeout to redirect to login page
      setTimeout(() => {
        router.push('/login?redirect=/appointment');
      }, 2000);
    }
    try {
      const result = await Appointment(values).unwrap();
      if (result?.success) {
        form.reset();
        setSelectedDoctor(null);
        form.setValue('doctorId', '');
        toast.success('Your appointment has been successfully created!');
      }
    } catch (err) {
      toast.error(
        'We encountered an error while creating your appointment. Please try again.',
      );
    }
  }

  const handleDoctorChange = (doctorId: string) => {
    const doctor = doctors?.find((doc: any) => doc._id === doctorId);
    setSelectedDoctor(doctor);
    // @ts-ignore
    form.setValue('date', undefined);
    form.setValue('time', '');
  };

  const isDateAvailable = (date: Date) => {
    if (!selectedDoctor) return false;
    const day = format(date, 'EEEE').toLowerCase();
    return selectedDoctor.availability.days.includes(day);
  };

  return (
    <div className="">
      {/* warning card for the user if user are not registered */}
      {!user && <WarningCard />}

      {/* Appointment Form */}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Patient Name</FormLabel>
                <FormControl>
                  <Input placeholder="Patient Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <PhoneInput
                      defaultCountry="BD"
                      placeholder="Enter phone number"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="sex"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gender</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select sex" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Age</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter your age"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value) : undefined,
                        )
                      }
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="doctorId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Choose Doctor</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleDoctorChange(value);
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a doctor" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {dIsLoading && (
                      <div className="flex items-center justify-center p-4">
                        <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    )}
                    {dIsError && !dIsLoading && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Unable to fetch doctor information. Please try again
                        later.
                      </div>
                    )}
                    {!dIsLoading && !dIsError && doctors?.length > 0
                      ? doctors.map((doctor: any) => (
                          <SelectItem key={doctor._id} value={doctor._id}>
                            <div className="flex items-center gap-3">
                              <img
                                src={imageSrc(doctor?.image)}
                                alt={doctor?.name}
                                className="h-8 w-8 rounded-full object-cover"
                                loading="lazy"
                              />
                              <div className="text-start">
                                <p className="font-medium">{doctor?.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doctor?.specialty}
                                </p>
                              </div>
                            </div>
                          </SelectItem>
                        ))
                      : !dIsLoading &&
                        !dIsError && (
                          <div className="p-4 text-center text-sm text-muted-foreground">
                            No doctors available. Please check back later.
                          </div>
                        )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel>Appointment Date</FormLabel>
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'h-10 w-full rounded-md border-none bg-white pl-3 text-left text-sm font-normal placeholder:text-muted-foreground hover:bg-white',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date() || !isDateAvailable(date)
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Time Slot</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!form.watch('date') || timeSlots.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {timeSlots.length > 0 ? (
                        timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot}>
                            {slot}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-sm text-muted-foreground">
                          No time slots available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason for Visit</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe your symptoms or reason for visit"
                    className="resize-none"
                    rows={5}
                    {...field}
                  />
                </FormControl>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Minimum 10 characters</span>
                  <span>{field.value?.length || 0}/500</span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {isError && (
            <AlertMessage
              type="error"
              message={
                // @ts-ignore
                error?.data?.errorMessage?.[0]?.message ||
                'An error occurred while booking your appointment.'
              }
            />
          )}

          {isSuccess && (
            <AlertMessage
              type="success"
              message="Thank you for scheduling with us. We'll contact you shortly to confirm your appointment details."
            />
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || !form.formState.isValid}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting Appointment...
              </>
            ) : (
              ' Request Appointment'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default AppointmentForm;
